import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import OrderIdentifiers from '../../components/OrderIdentifiers';
import { ORDER_IDENTIFIERS } from '../../lib/orderIdentifiers';
import { matchesSearch } from '../../lib/itemSearch';
import { useAuth } from '../../components/AuthContext';
import ReceiveSheetContent from '../../components/inventory/ReceiveSheetContent';
import Overlay from '../../components/Overlay';
import QtyStepper, { fieldBox, NEGATIVE_BOX, REQUEST_BOX } from '../../components/QtyStepper';
import QRScanOverlay from '../../components/QRScanOverlay';
import { isAdminManagerOrExecutive, canAdjustInventory, canSeeInventoryCost } from '../../lib/permissions';
import AdjustAlertNotice from '../../components/inventory/AdjustAlertNotice';
import { needsAttention } from '../../lib/stockStatus';
import { workOrderRefError, WORK_ORDER_REF_HINT, normalizeWorkOrderRef } from '../../lib/workOrderRef';
import { ADJUST_REASONS, REASON_OPENING_COUNT } from '../../lib/adjustReasons';
// #595 -- the retry is a real control: 40px with a finger, 32 with a mouse
// (#417 D2's floors). A "Try again" nobody can hit is not a way to try again.
import { useTier, controlHeight } from '../../lib/tiers';
import { submitMultiItemRestockRequest, lineName, lineUom } from '../../lib/materialRequests';
// #349, Session 79.22b.6.1g -- Use and Adjust save through the one shared copy.
import { submitUseStock, submitAdjustStock, ADJUST_REFUSED, USE_NEEDS_WO, adjustDoneLine } from '../../lib/stockMovements';
import { submitReceiveStock, resolveReceivePrintOption, fetchPackSnapshot } from '../../lib/materialReceive';
// #684, Session 79.22b.4 -- MAKE OR BUY, decided here for every door that lands
// on this screen: Request materials, the item page, the QR label, Stock Home's
// Request several, and this screen's own list, Search and Scan.
import {
  requestRoute, requestLineQty, splitRequestLines, requestSendSummary, requestLineTag,
  makeOwnBlockedSentence, rowShortBy, createTransfer,
} from '../../lib/branchTransfers';
import { onItsWayTo } from '../../lib/transferLoads';
import { canConfirmSupply } from '../../lib/canApprove';
import RequestTransferSheet from './RequestTransferSheet';
import MakeForShelfSheet from './MakeForShelfSheet';
import {
  IconArrowLeft,
  IconSearch,
  IconCheck,
  IconCamera,
  IconAlertTriangle,
  IconInfoCircle,
  IconHistory,
  IconX,
  IconSend,
  IconBriefcase,
  IconTrash,
} from '@tabler/icons-react';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];
const BRANCH_NAMES = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));
function branchNameOf(id) {
  return BRANCHES.find(b => String(b.id) === String(id))?.name || 'that branch';
}

// #684 -- said when the category tree did not load, in Stock Home's words
// (550/551 F1): the check failing is SAID, and Request stays reachable, because
// with no tree every item reads as bought.
const MAKE_CHECK_FAILED = 'We could not check which items a branch makes just now. Request still works.';

// #374 -- said when the rule refuses an adjustment: on the screen in place of
// the list and the sheet, and again at the save. One sentence in one place, so
// the screen and the save cannot drift into saying different things.
// #374's sentence now lives with the shared Adjust (#349), which refuses with it.

// #684 D -- a line's destination tag. Teal where a branch makes it, the
// purchasing amber where it is bought; each text is its ramp's darkest stop on
// its own fill.
const LINE_TAG = {
  make: { background: '#E1F5EE', color: '#085041' },
  buy: { background: '#FAEEDA', color: '#633806' },
};

// #369 / #364 — the reason list and the Opening count constant moved to
// src/lib/adjustReasons.js when #364 added an Adjust sheet to the item detail
// page. Named rather than repeated as a literal at the notification guard in
// submitAdjust(): a rename or a typo there would silently restore the 47-alert
// behaviour with nothing to fail on, so the two must not be able to drift.

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// #282, Session 78d. The width of the desktop content column, matched to
// RequestMaterialsPage.js's own `maxWidth: 1100` rather than invented — #282's
// entry is explicit that the correct width has to be read out of the sibling
// screens or this page becomes a third convention.
//
// The black header bar stays full-bleed and only its CONTENTS are constrained,
// which is the standard pattern for a sticky app bar and keeps the bar reading
// as page chrome rather than as a floating card.
// Moved to src/lib/layout.js at #356, which would otherwise have been this
// number's third independent home. See that file for the reasoning.
import { DESKTOP_COLUMN } from '../../lib/layout';

// #760, #793 - a cart row's box (approved drawing, section 4): 52 wide, the
// request's purple, 16px type; its height is the stepper's, from the pointer.
const CART_BOX = Object.freeze({
  flex: 'none', width: 52, borderRadius: 8, fontFamily: 'var(--font-body)',
  fontSize: 16, fontWeight: 700, letterSpacing: 'normal', ...REQUEST_BOX,
});

// #312, Session 78d, WIDENED at 79.14d for #304's D6/D16. It matched the item
// name and the three identifiers the page already fetched; it now matches all
// SIX an order carries, which needed two more columns on both selects above —
// `vendor_order_ref` and `invoice_ref`. A matcher reading a column nobody
// selected matches nothing and looks exactly like an order that is not there.
//
// It stays a LOCAL function rather than calling the shared matcher directly,
// because the rows here are flattened ITEMS with camelCase fields rather than
// request rows, and they carry one field no order has: the item's own name,
// which is what most people actually type on this screen. The field list is
// derived from ORDER_IDENTIFIERS so it cannot drift from the other four
// surfaces, and the name is added on top.
//
// Client-side by construction, not by convenience: these items are derived from
// a nested `material_requests` fetch, and CLAUDE.md's standing rule is that
// filtering a nested relation two or more hops deep returns empty silently with
// no error. Filtering the already-materialised list avoids that class entirely.
// #604 D7 -- the matcher moved to src/lib/itemSearch.js at Session 79.21c.
// It is pure logic with no database and no React, which is where every gate
// decision that can be got wrong SILENTLY actually lives -- and D7's order-
// number exception is exactly that kind of decision. Testable without
// mounting a page that imports supabase.

// #509, Session 79.21c -- ONE STABLE KEY FOR A ROW, because an off-catalog
// line has no catalog item to be keyed by. Everything on this screen was keyed
// on itemId, which is null for those lines: React would have collided every one
// of them onto the same key, and `completedIds` would have marked them all done
// together the moment one was received.
//
// The prefix matters. A bare request-line id could collide with a catalog
// item id, since both are integers from different tables.
function keyOf(item) {
  return item?.itemId != null ? String(item.itemId) : `ocl-${item?.requestItemId}`;
}

function ItemRow({ item, isCompleted, doneInfo, isReceive, isAdjust, isRequest, viewAll, isRequestAdd, requestLabel, onOpen }) {
  if (isCompleted) {
    return (
      <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#085041', textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
          <div style={{ fontSize: 11, color: '#0F6E56', marginTop: 1 }}>{doneInfo?.label}</div>
        </div>
        <div style={{ width: 28, height: 28, background: '#1D9E75', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconCheck size={15} color="white" />
        </div>
      </div>
    );
  }

  if (isReceive) {
    return (
      <div style={{ background: '#E6F1FB', border: '0.5px solid #B5D4F4', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0C447C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
          {/* #509, Session 79.21c -- the same words the multi-item delivery sheet
              already uses, so the two receive surfaces describe one thing
              identically. It also explains why this card shows no shelf count. */}
          {item.isOffCatalog && (
            <div style={{ fontSize: 11, color: '#9A5F00', fontWeight: 600, marginTop: 2 }}>
              Off-catalog — flagged for catalog review
            </div>
          )}
          {/* #312, Session 78d. The card showed the request number alone. #287 made
              the PO number generated, format-ruled and uniquely indexed, which is
              what turns it into a reliable key rather than hand-typed text — so the
              number the vendor and the paperwork both quote belongs on the screen
              where the delivery is booked in.

              The identifiers stay visibly separate and individually labelled, per
              CLAUDE.md: request_number is DB-generated and internal, po_ref is
              Purchasing's, work_order_ref is the requester's. Never concatenated.

              #304's D15 REVERSES THE LAST CLAUSE OF THIS COMMENT, at Session
              79.14d, and the old wording is kept here rather than deleted because
              it was right about the thing it was solving. It read: "each renders
              only when present rather than printing an empty separator." That
              reasoning is about PUNCTUATION — it was avoiding a dangling separator
              between two chips — and it is still right about punctuation. It is
              not a reason to let the field vanish. A labelled chip prints no stray
              separator and still tells the reader the number does not exist, so
              the two concerns come apart cleanly. Colby's ruling: "show something
              like a - or 'not recorded' if their not so it doesnt look like the
              layout problems to a user." */}
          <div style={{ fontSize: 11, color: '#185FA5', marginTop: 1 }}>{item.qtyRequested} {item.uom} requested</div>
          <OrderIdentifiers req={item.identifiers} omitEmpty style={{ marginTop: 4 }} />
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {/* #509, Session 79.21c. An off-catalog line has NO inventory_stock row,
              so qtyOnHand is null and this printed a bare " on hand" -- found by
              opening the screen, not by any test.

              #304's D15 already governs this exact shape on the identifier chips
              directly above, and it is Colby's own ruling: "show something like a
              - or 'not recorded' if their not so it doesnt look like the layout
              problems to a user." A missing number must SAY it is missing.

              "Not stocked" rather than "0 on hand", because zero is a count
              somebody keeps and this is the absence of a count entirely. */}
          <div style={{ fontSize: 11, color: item.qtyOnHand == null ? '#7A7A7A' : item.qtyOnHand <= 0 ? '#A32D2D' : '#9A5F00', fontWeight: 700, marginBottom: 4 }}>
            {item.qtyOnHand == null ? 'Not stocked' : `${item.qtyOnHand} on hand`}
          </div>
          <button onClick={() => onOpen(item)} style={{ background: '#1D9E75', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Receive</button>
        </div>
      </div>
    );
  }

  if (isAdjust) {
    return (
      <div style={{ background: viewAll ? 'white' : '#FCEBEB', border: `0.5px solid ${viewAll ? '#E8E8E8' : '#F09595'}`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: viewAll ? '#0F0F0F' : '#791F1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
          <div style={{ fontSize: 11, color: viewAll ? '#7A7A7A' : '#A32D2D', marginTop: 1 }}>{item.uom} · system shows {item.qtyOnHand}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: '#A32D2D', fontWeight: 700, marginBottom: 4 }}>{viewAll ? `${item.qtyOnHand} on hand` : 'Audit needed'}</div>
          <button onClick={() => onOpen(item)} style={{ background: '#0F0F0F', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Adjust</button>
        </div>
      </div>
    );
  }

  const isOut = item.qtyOnHand <= 0;
  const cardBg = isOut ? '#FCEBEB' : '#FAEEDA';
  const cardBorder = isOut ? '#F09595' : '#EF9F27';
  const nameColor = isOut ? '#791F1F' : '#412402';
  const subColor = isOut ? '#A32D2D' : '#633806';
  const statusLabel = isOut ? `${item.qtyOnHand} · Out` : `${item.qtyOnHand} · Low`;
  const statusColor = isOut ? '#A32D2D' : '#9A5F00';

  return (
    <div style={{ background: viewAll ? 'white' : cardBg, border: `0.5px solid ${viewAll ? '#E8E8E8' : cardBorder}`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: viewAll ? '#0F0F0F' : nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        <div style={{ fontSize: 11, color: viewAll ? '#7A7A7A' : subColor, marginTop: 1 }}>{item.uom}</div>
        {/* #509 -- the same words the multi-item delivery sheet already uses, so
            the two receive surfaces describe one thing identically. It explains
            why this row shows no shelf count: there is no shelf record to show. */}
        {item.isOffCatalog && (
          <div style={{ fontSize: 11, color: '#9A5F00', marginTop: 2 }}>
            Off-catalog — flagged for catalog review
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: statusColor, fontWeight: 700, marginBottom: 4 }}>
          {item.qtyOnHand == null ? 'Not stocked' : viewAll ? `${item.qtyOnHand} on hand` : statusLabel}
        </div>
        {/* #684 -- a made item's button says where the request goes, in Stock
            Home's words: "Ask Houston", or "Make these" at the maker. */}
        <button onClick={() => onOpen(item)} style={{ background: isRequestAdd ? '#1D9E75' : '#0F0F0F', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{isRequestAdd ? '+ Add' : isRequest ? (requestLabel || '+ Request') : isAdjust ? 'Adjust' : '− Use'}</button>
      </div>
    </div>
  );
}

export default function InventoryActionCatalog({ mode, setActive, preloadItemId, preloadSource, preloadCartItems }) {
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, requireJobRefOnUse } = useAuth();
  const canOverrideJobRef = isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive });
  const userBranchId = profile?.branch_id;
  const branchName = BRANCHES.find(b => b.id === userBranchId)?.name || '';
  const isReceive = mode === 'receive';
  const isUse = mode === 'use';
  const isAdjust = mode === 'adjust';
  const isRequest = mode === 'request';

  // #374, Session 79.22b.6.1f -- THE SCREEN THAT WRITES AN ADJUSTMENT ASKS THE
  // RULE ITSELF. The rule used to be checked only at the doors that lead here
  // (Stock Home's card, the item page, the scan sheet), while App.js routes
  // ?scan_action=adjust straight to this screen -- so a label scan was a way in
  // that no rule stood in front of. Gate the write surface, not the doors: the
  // doors were already right. Every role passes the rule today (#369), so nobody
  // sees a change; this is the checkpoint #363's threshold will stand on.
  // `profile` goes in with the five flags, per locked rule 8. While sign-in is
  // still loading there is no profile, and nothing is refused on a guess.
  const canAdjust = canAdjustInventory({ isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile });
  const adjustRefused = isAdjust && !!profile && !canAdjust;
  // #363 D12 -- the notice's dollar figure and limits, for people who may see
  // stock cost only. `profile` goes in: accounts payable reads it (locked rule 8).
  const canSeeCost = canSeeInventoryCost({ isAdmin, isManager, isExecutive, isPurchasing, profile });

  // #224. The shared sheet rendered as an unbranched Overlay variant="sheet", so on
  // desktop all four flows (request, receive, use, adjust) came up as a full-width
  // band pinned to the bottom of the viewport. isMobile did not exist anywhere in
  // this file; this is the useState/useEffect resize-listener pattern already live
  // in PrinterRegistryPage.js, applied at Session 67 for #64 - not a plain
  // window.innerWidth read, which never updates when the window is resized.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { coarse, phoneLayout } = useTier();   // #595; phoneLayout for #684's Make sheet
  // The shared stepper's size (40 to a finger, 32 to a mouse), for the cart
  // row's Remove button that sits beside it (#760, #513).
  const stepH = coarse ? controlHeight(true, 'min') : controlHeight(false, 'commit');
  const [viewAll, setViewAll] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [defaultItems, setDefaultItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [completedIds, setCompletedIds] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [error, setError] = useState('');
  // ** #595, Session 79.21b -- A LIST THAT DID NOT LOAD MUST NOT SAY THERE IS
  // NOTHING TO DO. ** A failed Supabase query does NOT throw: it returns
  // `{ data: null, error }`, so `(data || [])` is an empty list and the screen
  // renders "No open orders" or "All stocked up" -- the same words it shows
  // when nothing really is waiting. A person standing at a delivery on a weak
  // connection is told nothing is on order, with no reason to try again.
  // This flag separates "the load succeeded and found nothing" from "the load
  // did not happen", which is the whole defect.
  const [loadFailed, setLoadFailed] = useState(false);

  const [requestCart, setRequestCart] = useState([]);

  // #684 -- THE CATEGORY TREE, which decides make or buy. Request mode only, and
  // the screen waits for it before opening anything: a sheet that opens as a
  // purchase and changes a second later is how "Request" briefly offered
  // Purchasing for a Houston frame on Stock Home (550/551 callout 11).
  const [categories, setCategories] = useState([]);
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [categoriesFailed, setCategoriesFailed] = useState(false);
  // #684 A -- a made item opened from any door opens ITS OWN sheet: Ask the
  // maker, or Make these ourselves at the maker. `fromCart` marks one opened
  // from a line of a request already being built, which goes back to it.
  const [routedItem, setRoutedItem] = useState(null);
  const [routedAsks, setRoutedAsks] = useState([]);
  const [routedDone, setRoutedDone] = useState(null);
  // RequestTransferSheet calls onDone and then onClose in the same breath; the
  // ref lets onClose know the done card is up rather than leaving the screen.
  const routedDoneRef = useRef(false);
  // #684 F -- what a split send actually sent, for the done screen, and a
  // line of news on a request that is still being built.
  const [requestSent, setRequestSent] = useState([]);
  const [requestNotice, setRequestNotice] = useState('');

  useEffect(() => {
    if (!isRequest) return undefined;
    let live = true;
    (async () => {
      const { data, error: catErr } = await supabase
        .from('inventory_categories')
        .select('id, name, parent_id, manufactured_at_branch_id, manufacturing_department_id');
      if (!live) return;
      setCategoriesFailed(!!catErr);
      setCategories(data || []);
      setCategoriesReady(true);
    })();
    return () => { live = false; };
  }, [isRequest]);

  // #684 -- Stock Home's Request several. Each line is routed on arrival, so a
  // frame Houston makes goes to Houston rather than into a purchase (D), and
  // proposes what the row is SHORT by rather than the buy size (#615). Waits for
  // the tree, for the reason above.
  //
  // #687 -- A SELECTION OF ONE opens as ONE ITEM, through openItem(). It used to
  // arrive as a one-line cart with no active item, and the single-item view read
  // that missing item's on-hand count and blanked the whole app.
  useEffect(() => {
    if (!preloadCartItems || preloadCartItems.length === 0) return;
    if (isRequest && !categoriesReady) return;
    const items = preloadCartItems.map(i => ({
      itemId: i.itemId, name: i.itemName, uom: i.itemUom, sku: i.sku ?? null,
      categoryId: i.categoryId ?? null, qtyOnHand: i.qtyOnHand ?? null,
      reorderPoint: i.reorderPoint ?? null, reorderQty: i.reorderQty ?? null,
    }));
    if (items.length === 1) { openItem(items[0]); return; }
    setRequestCart(items.map(i => cartLineFor(i)));
    // openItem and cartLineFor are plain closures over this render's state; the
    // tree they read is what categoriesReady announces. This project's eslint
    // config does not carry react-hooks/exhaustive-deps.
  }, [preloadCartItems, categoriesReady]);
  const [requestAddingItem, setRequestAddingItem] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [requestWorkOrderRef, setRequestWorkOrderRef] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestDone, setRequestDone] = useState(false);
  const [requestError, setRequestError] = useState('');

  const [receiveQty, setReceiveQty] = useState(1);
  const [receiveJobRef, setReceiveJobRef] = useState('');
  const [receivePackingSlip, setReceivePackingSlip] = useState(null);
  const [receiveMatchedRequest, setReceiveMatchedRequest] = useState(null);
  // #279. null until a matched receive returns. Carries the database's own
  // all_complete so the done screen can say whether the order actually closed
  // instead of asserting that it did.
  const [receiveOrderClosed, setReceiveOrderClosed] = useState(null);
  // #497 - the receive saved but its packing slip photo did not upload.
  const [receivePhotoFailed, setReceivePhotoFailed] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [receiveDone, setReceiveDone] = useState(false);
  const [receiveError, setReceiveError] = useState('');
  const [receivePrintOption, setReceivePrintOption] = useState(null);
  const [receivePrintDescription, setReceivePrintDescription] = useState('Checking printer...');
  // #333. Seeded to packQty 1 — one label per unit, today's behaviour — so the
  // sheet never flashes a count the lookup is about to change.
  const [receivePack, setReceivePack] = useState({ packQty: 1, packUnit: null, vendorName: null });

  const [useQty, setUseQty] = useState(1);
  const [useJobRef, setUseJobRef] = useState('');
  const [useNotes, setUseNotes] = useState('');
  const [useJobHistory, setUseJobHistory] = useState(null);
  const [useJobHistoryLoading, setUseJobHistoryLoading] = useState(false);
  const [useSaving, setUseSaving] = useState(false);
  const [useDone, setUseDone] = useState(false);
  const [useError, setUseError] = useState('');

  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustReason, setAdjustReason] = useState(ADJUST_REASONS[0]);
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustDone, setAdjustDone] = useState(false);
  const [adjustError, setAdjustError] = useState('');
  // #363 D17 -- what the save did, so the done screen says it.
  const [adjustDoneText, setAdjustDoneText] = useState('');

  const packingSlipRef = useRef();

  useEffect(() => {
    if (!preloadItemId || !userBranchId || !profile) return;
    // #684 -- request mode decides make or buy before it opens anything.
    if (isRequest && !categoriesReady) return;

    async function preload() {
      setLoading(false);
      try {
        const { data: itemData } = await supabase
          .from('inventory_items')
          .select('id, name, sku, uom, is_active, category_id')
          .eq('id', preloadItemId)
          .single();
        if (!itemData) return;

        const { data: stockData } = await supabase
          .from('inventory_stock')
          .select('id, qty_on_hand, reorder_point, reorder_qty')
          .eq('item_id', preloadItemId)
          .eq('branch_id', userBranchId)
          .single();

        const qtyOnHand = stockData?.qty_on_hand ?? 0;
        const reorderQty = stockData?.reorder_qty ?? null;

        let requestNumber = null;
        let requestId = null;
        let qtyRequested = null;
        let requestedBy = null;
        let matchedReqFull = null;

        if (isReceive) {
          const { data: reqData } = await supabase
            .from('material_requests')
            .select(`
              id, request_number, requested_by, vendor_id,
              work_order_ref, po_ref, vendor_order_ref, invoice_ref,
              tracking_carrier, tracking_number, tracking_eta,
              material_request_items ( id, item_id, qty_requested, qty_received )
            `)
            .eq('branch_id', userBranchId)
            .eq('status', 'ordered')
            .order('created_at', { ascending: false });

          const matched = (reqData || []).find(req =>
            req.material_request_items?.some(mri => mri.item_id === preloadItemId)
          );
          if (matched) {
            const mri = matched.material_request_items.find(m => m.item_id === preloadItemId);
            requestNumber = matched.request_number;
            requestId = matched.id;
            qtyRequested = mri?.qty_requested;
            requestedBy = matched.requested_by;
            matchedReqFull = {
              id: matched.id,
              request_number: matched.request_number,
              requested_by: matched.requested_by,
              vendor_id: matched.vendor_id || null,
              branch_id: userBranchId,
              work_order_ref: matched.work_order_ref || null,
              po_ref: matched.po_ref || null,
              // #304 D16. The sheet is where somebody at a pallet confirms they
              // are booking against the right order, which is the moment the
              // numbers matter most — and it showed two of six.
              vendor_order_ref: matched.vendor_order_ref || null,
              invoice_ref: matched.invoice_ref || null,
              tracking_carrier: matched.tracking_carrier || null,
              tracking_number: matched.tracking_number || null,
              tracking_eta: matched.tracking_eta || null,
              // #274: carry only the line being received, as a single-element
              // array. ReceiveSheetContent reads material_request_items[0] for
              // both the "Open order found" strip and the QTY RECEIVED default,
              // so on a multi-item request the first element must be the matched
              // line rather than whichever line happened to sort first.
              material_request_items: mri
                ? [{ id: mri.id, item_id: mri.item_id, qty_requested: mri.qty_requested, qty_received: mri.qty_received ?? null }]
                : [],
            };
          }
        }

        openItem({
          itemId: itemData.id,
          name: itemData.name,
          sku: itemData.sku,
          uom: itemData.uom,
          qtyOnHand,
          reorderQty,
          // #684 -- what routes the item, and what it is short by.
          categoryId: itemData.category_id ?? null,
          reorderPoint: stockData?.reorder_point ?? null,
          // Stock Home's purchase path is always deliberate: a bought item's
          // own route, or "Buy from a vendor instead?" on a made one. It says
          // so ('buy'), and this screen does not send it back to the maker.
          buyAnyway: isRequest && preloadSource === 'buy',
          requestNumber,
          // #312. Both already fetched at the select above; only the card and the
          // filter were missing them.
          poRef: matchedReqFull?.po_ref || null,
          workOrderRef: matchedReqFull?.work_order_ref || null,
          requestId,
          qtyRequested,
          requestedBy,
          matchedReqFull,
        });
      } catch (e) {
        setError('Failed to load item.');
      }
    }

    preload();
  }, [preloadItemId, userBranchId, profile, isReceive, categoriesReady]);

  const loadDefaultItems = useCallback(async () => {
    if (!userBranchId) return;
    if (preloadItemId) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      if (isReceive) {
        const { data: reqData, error: reqErr } = await supabase
          .from('material_requests')
          .select(`
            id, request_number, status, branch_id, created_at,
            requested_by, vendor_id, work_order_ref, po_ref, vendor_order_ref, invoice_ref,
            tracking_carrier, tracking_number, tracking_eta,
            material_request_items ( id, item_id, qty_requested, qty_received,
              is_off_catalog, off_catalog_description, off_catalog_uom,
              inventory_items ( id, name, sku, uom, image_url )
            )
          `)
          .eq('branch_id', userBranchId)
          .eq('status', 'ordered')
          .order('created_at', { ascending: false });
        // #595 -- read the error. Without this line a failed request is
        // indistinguishable from a branch with nothing on order.
        if (reqErr) throw reqErr;

        const items = [];
        (reqData || []).forEach(req => {
          (req.material_request_items || []).forEach(mri => {
            // #509, Session 79.21c -- THIS `if` WAS THE WHOLE DEFECT. An
            // off-catalog line has item_id NULL by design, so the nested
            // inventory_items join returns null and the line was silently
            // skipped -- meaning an ordered off-catalog item never appeared on
            // this screen at all and could not be received. The order sat in
            // the database, ordered and waiting, while the screen refused to
            // draw it. The multi-item delivery sheet received the same order
            // correctly the whole time.
            const isOff = !!mri.is_off_catalog && mri.item_id == null;
            if (!mri.inventory_items && !isOff) return;
            items.push({
              itemId: mri.inventory_items?.id ?? null,
              // #509 -- what identifies an off-catalog line: its own row.
              requestItemId: mri.id,
              isOffCatalog: isOff,
              // lineName/lineUom are materialRequests.js's own helpers, now
              // exported. NOT a second copy of the is_off_catalog ternary:
              // #514 is the recorded cost of a caller reimplemented nearby.
              name: lineName(mri) || 'Off-catalog item',
              sku: mri.inventory_items?.sku ?? null,
              uom: lineUom(mri),
              requestNumber: req.request_number,
              poRef: req.po_ref || null,          // #312
              workOrderRef: req.work_order_ref || null,  // #312
              // #304 D16, 79.14d. The six numbers in the SNAKE_CASE shape the
              // shared block and the shared matcher both read, so this screen
              // cannot drift from the other four. Kept as a sub-object rather
              // than flattened alongside the camelCase fields above, so it is
              // obvious which fields are the shared contract and which are
              // this page's own.
              identifiers: {
                request_number: req.request_number || null,
                po_ref: req.po_ref || null,
                vendor_order_ref: req.vendor_order_ref || null,
                work_order_ref: req.work_order_ref || null,
                invoice_ref: req.invoice_ref || null,
                tracking_number: req.tracking_number || null,
              },
              requestId: req.id,
              qtyRequested: mri.qty_requested,
              requestedBy: req.requested_by,
              matchedReqFull: {
                id: req.id,
                request_number: req.request_number,
                requested_by: req.requested_by,
                vendor_id: req.vendor_id || null,
                branch_id: req.branch_id,
                work_order_ref: req.work_order_ref || null,
                po_ref: req.po_ref || null,
                vendor_order_ref: req.vendor_order_ref || null,   // #304 D16
                invoice_ref: req.invoice_ref || null,             // #304 D16
                tracking_carrier: req.tracking_carrier || null,
                tracking_number: req.tracking_number || null,
                tracking_eta: req.tracking_eta || null,
                // #274: carry only the line being received, as a single-element
                // array. ReceiveSheetContent reads material_request_items[0] for
                // both the "Open order found" strip and the QTY RECEIVED default,
                // so on a multi-item request the first element must be the matched
                // line rather than whichever line happened to sort first.
                material_request_items: [
                  // #509 -- the off-catalog fields travel with the line, so
                  // the sheet names it the same way the list just did.
                  { id: mri.id, item_id: mri.item_id, qty_requested: mri.qty_requested, qty_received: mri.qty_received ?? null,
                    is_off_catalog: !!mri.is_off_catalog,
                    off_catalog_description: mri.off_catalog_description ?? null,
                    off_catalog_uom: mri.off_catalog_uom ?? null },
                ],
              },
            });
          });
        });

        // #509 -- an off-catalog line has no inventory_stock row to ask about.
        // Without the filter this fires `.eq('item_id', null).single()` once per
        // off-catalog line, and `.single()` on no rows is an ERROR rather than an
        // empty result -- so one off-catalog order would have failed the whole
        // list, which is the defect the screen already had wearing a new hat.
        const stockRes = await Promise.all(
          [...new Set(items.map(i => i.itemId).filter(id => id != null))].map(id =>
            supabase.from('inventory_stock').select('item_id, qty_on_hand').eq('item_id', id).eq('branch_id', userBranchId).single()
          )
        );
        const stockMap = {};
        stockRes.forEach(r => { if (r.data) stockMap[r.data.item_id] = r.data.qty_on_hand; });
        // An off-catalog line keeps qtyOnHand null rather than 0: there is no
        // shelf record, and "0 on hand" would state a count nobody keeps.
        setDefaultItems(items.map(i => ({
          ...i,
          qtyOnHand: i.itemId == null ? null : (stockMap[i.itemId] ?? 0),
        })));

      // #445, Session 78o. `isRequest` was missing from this chain entirely, so
      // `defaultItems` was never assigned in request mode and `remainingCount`
      // was structurally zero on every render, for every branch, whatever the
      // shelf held. The screen was fully dressed for a list it never received —
      // a Low + Out filter chip, a header counting items, and an "All stocked
      // up" empty state — and none of the three could ever say anything else.
      //
      // That is #357's failure reappearing on a different screen. Its header
      // records the consequence exactly: "Dallas would have opened Stock Home on
      // Sep 1 and read 'All stocked up' over an empty warehouse." Request
      // restock was printing that sentence.
      //
      // Request wants the SAME set as Use — what needs attention at this branch,
      // out-of-stock first — so it shares the branch rather than growing a fourth
      // copy of a rule that already had three disagreeing versions once.
      } else if (isUse || isRequest) {
        const { data, error: stockErr } = await supabase
          .from('inventory_stock')
          .select(`
            id, qty_on_hand, reorder_point, reorder_qty,
            inventory_items ( id, name, sku, uom, is_active, category_id )
          `)
          .eq('branch_id', userBranchId);
        if (stockErr) throw stockErr;   // #595

        const flagged = (data || [])
          .filter(row => {
            if (!row.inventory_items?.is_active) return false;
            // #357 — this inline rule was the only CORRECT one of the three, and
            // it is the shape src/lib/stockStatus.js was built from. Routed
            // through the shared helper anyway: leaving the one right copy inline
            // leaves a fourth chance to diverge, and the whole defect was three
            // screens answering the same question differently.
            return needsAttention(row.qty_on_hand, row.reorder_point);
          })
          .sort((a, b) => {
            const aOut = a.qty_on_hand <= 0;
            const bOut = b.qty_on_hand <= 0;
            if (aOut && !bOut) return -1;
            if (!aOut && bOut) return 1;
            return a.qty_on_hand - b.qty_on_hand;
          })
          .map(row => ({
            itemId: row.inventory_items.id,
            stockId: row.id,
            name: row.inventory_items.name,
            uom: row.inventory_items.uom,
            qtyOnHand: row.qty_on_hand,
            reorderPoint: row.reorder_point,
            // #684 -- selected above and never carried, so a row opened from
            // this list proposed 1 and said "No reorder qty set". Latent: the
            // plain list only ever shows behind an open request sheet today.
            reorderQty: row.reorder_qty,
            // #684 -- what routes the row: made, or bought.
            categoryId: row.inventory_items.category_id ?? null,
            isOut: row.qty_on_hand <= 0,
          }));
        setDefaultItems(flagged);

      } else if (isAdjust) {
        const { data, error: adjErr } = await supabase
          .from('inventory_stock')
          .select(`
            id, qty_on_hand,
            inventory_items ( id, name, sku, uom, is_active )
          `)
          .eq('branch_id', userBranchId)
          .lt('qty_on_hand', 0);
        if (adjErr) throw adjErr;   // #595

        const flagged = (data || [])
          .filter(row => row.inventory_items?.is_active)
          .sort((a, b) => a.qty_on_hand - b.qty_on_hand)
          .map(row => ({
            itemId: row.inventory_items.id,
            stockId: row.id,
            name: row.inventory_items.name,
            uom: row.inventory_items.uom,
            qtyOnHand: row.qty_on_hand,
          }));
        setDefaultItems(flagged);
      }
    } catch (e) {
      // #595 -- the list did not load. Say that, and say nothing about what is
      // or is not waiting at this branch, because we do not know.
      setLoadFailed(true);
      setDefaultItems([]);
    } finally {
      setLoading(false);
    }
  }, [userBranchId, isReceive, isUse, isAdjust, preloadItemId]);

  const loadAllItems = useCallback(async () => {
    if (!userBranchId) return;
    try {
      const { data } = await supabase
        .from('inventory_stock')
        .select(`
          id, qty_on_hand, reorder_qty, reorder_point,
          inventory_items ( id, name, sku, uom, is_active, category_id )
        `)
        .eq('branch_id', userBranchId);

      const items = (data || [])
        .filter(row => row.inventory_items?.is_active)
        .map(row => ({
          itemId: row.inventory_items.id,
          stockId: row.id,
          name: row.inventory_items.name,
          uom: row.inventory_items.uom,
          qtyOnHand: row.qty_on_hand,
          reorderQty: row.reorder_qty,
          // #684 -- the route, and the floor the shortfall is measured from.
          reorderPoint: row.reorder_point,
          categoryId: row.inventory_items.category_id ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllItems(items);
    } catch (e) {}
  }, [userBranchId]);

  useEffect(() => { loadDefaultItems(); }, [loadDefaultItems]);
  // #604 D2 -- the all-items fetch is triggered by the first keystroke as well
  // as by the browse button. loadAllItems() is the same call Browse all items
  // has always made, and its existing loading and error states (#595) are
  // reused rather than rewritten.
  useEffect(() => {
    if (viewAll || search.trim()) loadAllItems();
  }, [viewAll, search, loadAllItems]);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // #684 -- ONE LINE OF A REQUEST, built one way from every door. The route
  // decides what it proposes (#615: a made line the shortfall, a bought one the
  // buy size), and the line carries its own on-hand count and floor, so the
  // single-item view never has to borrow them from an item it may not have
  // (#687).
  function cartLineFor(item, buyAnyway = false) {
    const r = requestRoute({ categories, categoryId: item.categoryId, viewerBranchId: userBranchId });
    const shortBy = rowShortBy({ qty_on_hand: item.qtyOnHand, reorder_point: item.reorderPoint ?? null });
    return {
      itemId: item.itemId,
      itemName: item.name,
      itemUom: item.uom,
      sku: item.sku ?? null,
      categoryId: item.categoryId ?? null,
      qtyOnHand: item.qtyOnHand ?? null,
      reorderPoint: item.reorderPoint ?? null,
      reorderQty: item.reorderQty ?? null,
      shortBy,
      buyAnyway: !!buyAnyway,
      qty: requestLineQty({ route: buyAnyway ? 'buy' : r.route, shortBy, reorderQty: item.reorderQty }),
    };
  }

  // #684 A -- open a made item's own sheet. At the maker, the shop's open asks
  // for it are read FIRST, because the Make sheet takes them into its opening
  // quantity once, at mount -- "so Make more does not quietly ask for the same
  // 14 again" -- and Stock Home hands it the same entries.
  async function openRouted(item) {
    routedDoneRef.current = false;
    setRoutedDone(null);
    setRequestNotice('');
    let asks = [];
    if (item.route === 'make_own') {
      const { data } = await supabase
        .from('branch_transfers')
        .select(`
          id, transfer_number, status, from_branch_id, to_branch_id, fulfilment_mode,
          confirmed_date, requested_at,
          branch_transfer_items!inner ( item_id, qty_requested, qty_ready, qty_shipped, qty_received, line_status, promised_date )
        `)
        .in('status', ['awaiting_confirmation', 'approved'])
        .eq('to_branch_id', userBranchId)
        .eq('branch_transfer_items.item_id', item.itemId);
      asks = (onItsWayTo(data || [], userBranchId)[item.itemId] || [])
        .filter(e => e.kind === 'made_here' || e.kind === 'asked_here');
    }
    setRoutedAsks(asks);
    setRoutedItem(item);
  }

  function openItem(itemData) {
    // #684 A -- A MADE ITEM OPENS ITS OWN SHEET here, as from every other door,
    // unless the person has chosen to buy it anyway. This screen is where six
    // doors land, which is why the decision lives here rather than at each.
    if (isRequest && !itemData.buyAnyway) {
      const r = requestRoute({ categories, categoryId: itemData.categoryId, viewerBranchId: userBranchId });
      if (r.route !== 'buy') {
        setActiveItem(null);
        setRequestCart([]);
        openRouted({ ...itemData, ...r });
        return;
      }
    }
    setActiveItem(itemData);
    setReceiveDone(false);
    setReceiveError('');
    setReceivePackingSlip(null);
    setReceiveMatchedRequest(null);
    setUseDone(false);
    setUseError('');
    setUseJobRef('');
    setUseNotes('');
    setUseJobHistory(null);
    setAdjustDone(false);
    setAdjustDoneText('');
    setAdjustError('');
    setAdjustQty(Math.max(0, itemData.qtyOnHand < 0 ? 0 : itemData.qtyOnHand));
    setAdjustReason(ADJUST_REASONS[0]);
    setAdjustNotes('');
    setRequestDone(false);
    setRequestError('');
    setRequestWorkOrderRef('');
    setRequestNotes('');
    setRequestAddingItem(false);
    setRequestSent([]);
    setRequestNotice('');
    setRequestCart([cartLineFor(itemData, !!itemData.buyAnyway)]);

    if (isReceive) {
      setReceiveJobRef(itemData.requestNumber || '');
      setReceiveQty(itemData.qtyRequested ?? 1);
      setReceivePrintOption(null);
      setReceivePrintDescription('Checking printer...');
      resolveReceivePrintOption(branchName).then(({ option, description }) => {
        setReceivePrintOption(option);
        setReceivePrintDescription(description);
      });
      // #333. Unlike InventoryItemDetail, this path already knows the matched
      // order at open time, so the pack size is read here rather than from an
      // effect. The synthetic fallback request built below carries no vendor_id
      // by construction, and passing null is correct there: with no order there
      // is no vendor to prefer, and fetchPackSnapshot falls back to the item's
      // preferred vendor exactly as the cost snapshot does.
      setReceivePack({ packQty: 1, packUnit: null, vendorName: null });
      fetchPackSnapshot(itemData.itemId, itemData.matchedReqFull?.vendor_id || null)
        .then(setReceivePack);
      if (itemData.matchedReqFull) {
        setReceiveMatchedRequest(itemData.matchedReqFull);
      } else if (itemData.requestNumber) {
        setReceiveMatchedRequest({
          id: itemData.requestId,
          request_number: itemData.requestNumber,
          requested_by: itemData.requestedBy,
          branch_id: userBranchId,
          work_order_ref: null,
          po_ref: null,
          vendor_order_ref: null,   // #304 D16
          invoice_ref: null,        // #304 D16
          tracking_carrier: null,
          tracking_number: null,
          tracking_eta: null,
        });
      }
    } else if (isUse) {
      setUseQty(1);
    }
  }

  function closeSheet() {
    setActiveItem(null);
    setReceiveDone(false);
    setUseDone(false);
    setAdjustDone(false);
    setAdjustDoneText('');
    setRequestAddingItem(false);
    setRequestCart([]);
    setRequestDone(false);
    setRequestSent([]);
    setRequestNotice('');
    if (preloadItemId || (preloadCartItems && preloadCartItems.length > 0)) setActive('inv_home');
  }

  const openedForOneItem = !!preloadItemId || !!(preloadCartItems && preloadCartItems.length > 0);

  // #684 -- the routed sheet closed WITHOUT sending: back to the request it
  // came from, back to this list, or back out when this screen was opened for
  // it alone -- exactly where closing the purchase sheet goes.
  function closeRouted() {
    const it = routedItem;
    setRoutedItem(null);
    if (routedDoneRef.current || it?.fromCart) return;
    if (openedForOneItem) setActive('inv_home');
  }

  // #684 -- the routed sheet SENT. A line of a request being built comes off it
  // with a line of news; anything else gets the done card, because a sheet that
  // closes on success and says nothing leaves the person guessing.
  function routedSent(title, sub, label) {
    const it = routedItem;
    setCompletedIds(prev => ({ ...prev, [keyOf(it)]: { label } }));
    setRoutedItem(null);
    if (it?.fromCart) {
      const remaining = requestCart.filter(l => String(l.itemId) !== String(it.itemId));
      setRequestCart(remaining);
      // With nothing left to send, the request is finished: the done card, not
      // an empty list.
      if (remaining.length > 0) { setRequestNotice(`${title}: ${it.name}.`); return; }
    }
    routedDoneRef.current = true;
    setRoutedDone({ title, sub });
  }

  function onAskSent() {
    const maker = branchNameOf(routedItem?.makerBranchId);
    routedSent(`${maker} has been asked`, `Your manager approves it first, then ${maker} confirms.`, `Asked ${maker}`);
  }

  function onMakeOwnSent() {
    const it = routedItem;
    if (makeOwnCanConfirm(it)) {
      routedSent('Added to To make', 'It is on our own shop’s list now.', 'Added to To make');
    } else {
      routedSent('Our shop has been asked', `${branchName}'s branch manager or fabrication manager confirms it and sets a date.`, 'Asked our shop');
    }
  }

  function makeOwnCanConfirm(it) {
    return canConfirmSupply(profile, {
      from_branch_id: userBranchId, to_branch_id: userBranchId,
      fulfilment_mode: 'make', manufacturing_department_id: it?.makerDepartmentId ?? null,
    });
  }

  // #684 B -- "Buy from a vendor instead?" from the routed sheet. A line of a
  // request being built simply switches to buying; an item opened on its own
  // opens the purchase sheet, marked as bought on purpose.
  function buyInstead() {
    const it = routedItem;
    setRoutedItem(null);
    if (!it) return;
    if (it.fromCart) {
      setRequestCart(prev => prev.map(l => String(l.itemId) === String(it.itemId) ? { ...l, buyAnyway: true } : l));
      return;
    }
    openItem({ ...it, buyAnyway: true });
  }

  // #684 E -- the other direction, from a one-item purchase sheet holding a
  // made item bought on purpose: back to asking the maker.
  function askInstead() {
    const line = requestCart[0];
    if (!line) return;
    setActiveItem(null);
    setRequestCart([]);
    openItem({
      itemId: line.itemId, name: line.itemName, uom: line.itemUom, sku: line.sku,
      categoryId: line.categoryId, qtyOnHand: line.qtyOnHand, reorderPoint: line.reorderPoint,
      reorderQty: line.reorderQty, buyAnyway: false,
    });
  }

  // #684 E -- one line of a request switched between its maker and buying.
  function setLineBuyAnyway(index, value) {
    setRequestCart(prev => prev.map((l, i) => (i === index ? { ...l, buyAnyway: value } : l)));
  }

  // The done card's own button: out the way closing the purchase sheet goes.
  function finishRouted() {
    routedDoneRef.current = false;
    setRoutedDone(null);
    closeSheet();
  }

  async function fetchJobHistory(jobRef) {
    if (!jobRef.trim() || !activeItem) { setUseJobHistory(null); return; }
    setUseJobHistoryLoading(true);
    try {
      const { data } = await supabase
        .from('inventory_transactions')
        .select('id, qty, created_at')
        .eq('item_id', activeItem.itemId)
        .eq('tx_type', 'consume')
        .eq('work_order_ref', jobRef.trim());

      if (data && data.length > 0) {
        const totalUsed = data.reduce((sum, tx) => sum + Math.abs(tx.qty), 0);
        const lastDate = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at;
        setUseJobHistory({ totalUsed, txCount: data.length, lastDate });
      } else {
        setUseJobHistory(null);
      }
    } catch (e) {
      setUseJobHistory(null);
    } finally {
      setUseJobHistoryLoading(false);
    }
  }

  async function submitReceive({ qty, jobRef, notes, packingSlip, matchedRequest, printLabels, labelCopies }) {
    if (!activeItem || !profile) return;
    setReceiveSaving(true);
    setReceiveError('');
    setReceiveOrderClosed(null);
    setReceivePhotoFailed(false);
    try {
      const result = await submitReceiveStock({
        item: { id: activeItem.itemId, name: activeItem.name, sku: activeItem.sku, uom: activeItem.uom },
        // #509 -- an off-catalog line is settled against its own request row.
        requestItemId: activeItem.requestItemId ?? null,
        isOffCatalog: !!activeItem.isOffCatalog,
        branchId: userBranchId,
        branchName,
        qty,
        jobRef,
        notes,
        packingSlip,
        matchedRequest,
        printLabels,
        printOption: receivePrintOption,
        labelCopies,
        profile,
      });

      // #279. This chip said "closed" on every matched receive, which stopped
      // being true the moment a partial receive left the order open. It now
      // reports what the receive actually did to the order.
      const orderClosed = matchedRequest ? result?.allComplete !== false : null;
      setReceiveOrderClosed(orderClosed);
      // #497 — the receive succeeded; the photo may not have. The sheet says so on
      // the success screen rather than the receive being thrown away.
      setReceivePhotoFailed(result?.photoFailed === true);
      setCompletedIds(prev => ({
        ...prev,
        // #509 -- keyOf, not itemId: every off-catalog line is null there.
        [keyOf(activeItem)]: {
          label: `Received +${qty}${matchedRequest ? ' · ' + matchedRequest.request_number + (orderClosed ? ' closed' : ' still open') : ''}`,
        },
      }));
      setReceiveDone(true);
      await loadDefaultItems();
      if (viewAll) await loadAllItems();
    } catch (e) {
      setReceiveError(e.message);
    } finally {
      setReceiveSaving(false);
    }
  }

  // #349, Session 79.22b.6.1g -- the write, its checks, the stock record (#789)
  // and the alert all live in src/lib/stockMovements.js, shared with the item
  // page. A refusal comes back as the sentence to show (drawing D, D2-D4).
  async function submitUse() {
    if (!activeItem || !profile) return;
    setUseSaving(true);
    setUseError('');
    try {
      const result = await submitUseStock({
        item: { id: activeItem.itemId, name: activeItem.name },
        branchId: userBranchId,
        branchName,
        qty: Number(useQty),
        jobRef: useJobRef,
        notes: useNotes,
        profile,
        requireJobRef: requireJobRefOnUse,
      });
      if (result.refused) { setUseError(result.refused); return; }
      const qty = Number(useQty);

      setCompletedIds(prev => ({ ...prev, [activeItem.itemId]: { label: `Used −${qty}` } }));
      setUseDone(true);
      await loadDefaultItems();
      if (viewAll) await loadAllItems();
    } catch (e) {
      setUseError('Something went wrong: ' + e.message);
    } finally {
      setUseSaving(false);
    }
  }

  async function submitAdjust() {
    if (!activeItem || !profile) return;
    // #374 -- the write itself refuses, before anything is read or inserted, so
    // no route to this function can post an adjustment the rule would refuse.
    // It says why rather than doing nothing (#492's standard).
    if (!canAdjust) { setAdjustError(ADJUST_REFUSED); return; }
    setAdjustSaving(true);
    setAdjustError('');
    try {
      // #349 -- the shared Adjust checks the role itself as well (#374), refuses
      // a negative count, creates a missing stock record (#789) and alerts as
      // before (#369's Opening count exception, #500's recipients).
      const result = await submitAdjustStock({
        auth: { isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile },
        item: { id: activeItem.itemId, name: activeItem.name },
        branchId: userBranchId,
        branchName,
        newQty: adjustQty === '' ? '' : Number(adjustQty),
        reason: adjustReason,
        notes: adjustNotes,
        profile,
      });
      if (result.refused) { setAdjustError(result.refused); return; }
      const { currentQty, newQty } = result;
      setAdjustDoneText(adjustDoneLine(result));

      setCompletedIds(prev => ({
        ...prev,
        [activeItem.itemId]: {
          label: `${currentQty} → ${newQty} · ${adjustReason}`,
        },
      }));
      setAdjustDone(true);
      await loadDefaultItems();
      if (viewAll) await loadAllItems();
    } catch (e) {
      setAdjustError('Something went wrong: ' + e.message);
    } finally {
      setAdjustSaving(false);
    }
  }

  function updateCartQty(index, qty) {
    setRequestCart(prev => prev.map((item, i) => i === index ? { ...item, qty } : item));
  }

  function removeCartItem(index) {
    setRequestCart(prev => prev.filter((_, i) => i !== index));
  }

  function startAddItemViaSearch() {
    setRequestAddingItem(true);
    setViewAll(true);
    setSearch('');
  }

  function startAddItemViaScan() {
    setShowScanner(true);
  }

  function extractScannedItemId(decoded) {
    let pathname = decoded;
    try {
      pathname = new URL(decoded).pathname;
    } catch {
      pathname = decoded;
    }
    const match = pathname.match(/\/inventory\/scan\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  async function handleCartScan(decoded) {
    setShowScanner(false);
    const scannedId = extractScannedItemId(decoded);
    if (!scannedId) {
      setRequestError("That code isn't an inventory item — try Search catalog instead.");
      return;
    }
    try {
      const { data: itemData } = await supabase
        .from('inventory_items')
        .select('id, name, sku, uom, is_active, category_id')
        .eq('id', scannedId)
        .single();
      if (!itemData || !itemData.is_active) {
        setRequestError('That item could not be found or is inactive.');
        return;
      }
      // #684 -- the count and floor ride along so a made line proposes what
      // this branch is short by, and the category so it knows it is made.
      const { data: stockData } = await supabase
        .from('inventory_stock')
        .select('qty_on_hand, reorder_point, reorder_qty')
        .eq('item_id', scannedId)
        .eq('branch_id', userBranchId)
        .single();
      addItemToCart({
        itemId: itemData.id, name: itemData.name, uom: itemData.uom, sku: itemData.sku,
        categoryId: itemData.category_id ?? null,
        qtyOnHand: stockData?.qty_on_hand ?? null,
        reorderPoint: stockData?.reorder_point ?? null,
        reorderQty: stockData?.reorder_qty ?? null,
      });
    } catch (e) {
      setRequestError('Something went wrong reading that scan: ' + e.message);
    }
  }

  // #684 -- a made item JOINS the request, tagged with where it is going (D);
  // it does not open its own sheet here, because the person is building a list
  // and the list sends each line to whoever supplies it (F).
  function addItemToCart(item) {
    setRequestCart(prev => {
      const line = cartLineFor(item);
      const existingIdx = prev.findIndex(ci => ci.itemId === line.itemId);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], qty: next[existingIdx].qty + line.qty };
        return next;
      }
      return [...prev, line];
    });
    setRequestAddingItem(false);
  }

  // #684 F -- ONE SEND, ONE REQUEST PER DESTINATION. Each maker gets its own
  // ask and Purchasing gets the rest, said before anything goes (the notice
  // above the button). In order, never together: createTransfer() takes a number
  // from a per-branch sequence and sends mail, and two requests are two
  // conversations -- RequestTransferSheet's D69 rule, word for word.
  //
  // WHAT ALREADY WENT IS TAKEN OFF THE LIST as it goes, so a failure part-way
  // names what was sent and a second press sends only the rest. Telling
  // somebody "it failed" when Houston has already been asked is how the same
  // material gets requested twice.
  async function submitRequest() {
    if (!profile || requestCart.length === 0) return;
    if (requestCart.some(item => !item.qty || item.qty < 1)) { setRequestError('Please enter a valid quantity for every item.'); return; }
    const split = splitRequestLines(requestCart, { categories, viewerBranchId: userBranchId });
    const blocked = makeOwnBlockedSentence(split.makeOwn);
    if (blocked) { setRequestError(blocked); return; }
    const wo = requestWorkOrderRef.trim();
    // An ask carries the work order the database checks; a purchase never
    // checked it. Refused here, in the shop's own words, rather than by Postgres.
    if (split.asks.length && wo && workOrderRefError(wo)) { setRequestError(workOrderRefError(wo)); return; }

    setRequestSaving(true);
    setRequestError('');
    setRequestNotice('');
    const sent = [];
    try {
      for (const g of split.asks) {
        const result = await createTransfer({
          fromBranchId: g.makerBranchId,
          toBranchId: userBranchId,
          mode: 'make',
          lines: g.lines.map(l => ({ item_id: l.itemId, qty_requested: Number(l.qty), name: l.itemName, uom: l.itemUom })),
          neededBy: null,
          workOrderRef: wo ? normalizeWorkOrderRef(wo) : null,
          notes: requestNotes.trim() || null,
          branchNames: BRANCH_NAMES,
          requesterName: profile.full_name || null,
        });
        if (result.error) throw result.error;
        sent.push({ kind: 'ask', makerBranchId: g.makerBranchId, lines: g.lines });
        setRequestCart(prev => prev.filter(l => !g.lines.some(x => String(x.itemId) === String(l.itemId))));
      }
      if (split.buy.length) {
        const r = await submitMultiItemRestockRequest({
          branchId: userBranchId,
          branchName,
          items: split.buy.map(item => ({ itemId: item.itemId, itemName: item.itemName, itemUom: item.itemUom, qty: item.qty })),
          workOrderRef: wo || null,
          notes: requestNotes.trim() || null,
          requestedBy: { id: profile.id, full_name: profile.full_name },
        });
        sent.push({ kind: 'buy', lines: split.buy, requestNumber: r?.requestNumber || null });
      }

      setCompletedIds(prev => {
        const next = { ...prev };
        sent.forEach(s => s.lines.forEach(item => {
          next[item.itemId] = {
            label: s.kind === 'ask'
              ? `Asked ${branchNameOf(s.makerBranchId)} ×${item.qty}`
              : s.lines.length === 1
                ? `Requested ×${item.qty}${wo ? ' · ' + wo : ''}`
                : `Requested ×${item.qty} · part of ${s.lines.length}-item request`,
          };
        }));
        return next;
      });
      setRequestSent(sent);
      setRequestDone(true);
      await loadDefaultItems();
      if (viewAll) await loadAllItems();
    } catch (e) {
      setRequestError(sent.length
        ? `${sent.length} request${sent.length === 1 ? '' : 's'} went through, then this failed: ${e.message}. What is still listed has not been sent.`
        : 'Something went wrong: ' + e.message);
    } finally {
      setRequestSaving(false);
    }
  }

  const useQtyInt = parseInt(useQty) || 0;
  const useWillGoNegative = activeItem ? (activeItem.qtyOnHand - useQtyInt) < 0 : false;
  const useNewQty = activeItem ? activeItem.qtyOnHand - useQtyInt : 0;

  // #312, Session 78d. Both lists are filtered now, and the open-orders list is
  // the one that matters: it is where the receive CARDS live, and it had no search
  // at all. `allItems` is built from inventory_stock and carries no request
  // context, so a PO or WO term simply finds nothing there — which is correct
  // rather than broken, because a catalog item that is not on order has no PO.
  // #604 D2, Session 79.21c -- THE DECISION THIS GATE EXISTS FOR.
  //
  // The default branch filters `defaultItems`, which is the LOW list. So a
  // search box on the default view would have returned nothing for exactly the
  // item the person opened it to find, and an empty result reads as "we do not
  // stock that" -- a promise the system cannot keep, which this project ranks
  // as worse than not offering the control. D1 WITHOUT D2 IS WORSE THAN
  // NEITHER.
  //
  // Empty box: what is low, unchanged (D3). First keystroke: everything this
  // branch stocks. The person never chooses a scope, and the sentence above the
  // list says which one they are looking at.
  const searching = search.trim().length > 0;
  const showingAll = viewAll || searching;
  // ** FOUND BY LIVE TEST AT 79.21c, AND D2 ALONE WOULD HAVE BROKEN RECEIVING. **
  //
  // On the other three screens `allItems` is a superset of the low list, so
  // escalating to it on a keystroke loses nothing. ON RECEIVE STOCK IT IS NOT.
  // That screen's default list is OPEN ORDERS -- rows carrying the request, PO
  // and work-order numbers #312 put there -- and `allItems` is built from
  // inventory_stock, which carries none of them. So searching `794002` returned
  // NO MATCHES while two cards bearing work order 794002 sat on the screen
  // behind the box, and order-number search is the thing that screen's search
  // was built FOR.
  //
  // The union, deduped by the same key the rows are keyed on. Open orders come
  // first because somebody typing an order number is looking for the delivery,
  // not for the catalog entry.
  const searchPool = !showingAll ? defaultItems
    : !isReceive ? allItems
      : (() => {
          const seen = new Set(defaultItems.map(keyOf));
          return [...defaultItems, ...allItems.filter(a => !seen.has(keyOf(a)))];
        })();
  const displayItems = searchPool.filter(i => matchesSearch(i, search));

  const remainingCount = defaultItems.filter(i => !completedIds[keyOf(i)]).length;

  function getHeaderBadge() {
    if (viewAll) return null;
    if (isReceive) return { bg: '#085041', color: '#9FE1CB', label: 'Open orders' };
    if (isUse) return { bg: '#633806', color: '#FAC775', label: 'Low + Out' };
    // #375, Session 78j. This pill used to render unconditionally, as a mode
    // label. The result on staging was the header reading
    // `Adjust count · Houston · 0 items need audit` with a red Audit needed
    // pill beside it, sitting directly above `No audit items`.
    //
    // Red is the strongest signal this UI has, and spending it on a screen that
    // is simultaneously reporting nothing to do is how a real audit badge stops
    // being read -- the same "trains everyone to ignore it" failure #369 fixed
    // on the notification side. So this one is count-driven, unlike the mode
    // labels above it, which are deliberately left alone: teal and amber are not
    // alarm colours and carry no urgency to spend.
    if (isAdjust) return remainingCount > 0 ? { bg: '#791F1F', color: '#F7C1C1', label: 'Audit needed' } : null;
    if (isRequest) return { bg: '#26215C', color: '#CECBF6', label: 'Low + Out' };
  }

  function getHeaderBackLabel() {
    if (isReceive) return 'Open orders';
    if (isUse) return 'Low + Out';
    if (isRequest) return 'Low + Out';
    return 'Audit needed';
  }

  function getEmptyState() {
    if (isReceive) return { title: 'No open orders', sub: `No items waiting to be received at ${branchName}` };
    if (isUse) return { title: 'All stocked up', sub: `No low or out-of-stock items at ${branchName}` };
    if (isRequest) return { title: 'All stocked up', sub: `No low or out-of-stock items at ${branchName}` };
    return { title: 'No audit items', sub: `No negative stock items at ${branchName}` };
  }

  function getHeaderSubtitle() {
    if (preloadItemId && activeItem) return `${activeItem.name} · ${branchName}`;
    if (preloadItemId && routedItem) return `${routedItem.name} · ${branchName}`;   // #684
    // #716, Session 79.22b.6d -- NO COUNT UNTIL THE LIST HAS ARRIVED. Before the
    // load finishes every count below is 0, so Request restock read "Dallas · 0
    // items need attention" for two or three seconds before it said 25: false,
    // and a person who glanced and left had been told nothing was low. The
    // branch alone, exactly as a failed load reads (#595) -- above the search
    // line too, whose "0 matches" is the same claim.
    if (loading) return branchName;
    // #604 callout A and B -- the list has never said it was FILTERED, and an
    // empty low list and an empty catalog looked identical (#445's exact
    // failure). It now names the set, and says when the set changes.
    if (searching) {
      const n = displayItems.length;
      return `Searching all items at ${branchName} — ${n} match${n === 1 ? '' : 'es'}`;
    }
    if (viewAll) return `${branchName} · all items`;
    // #595 -- found by forcing the failure on a live screen rather than by
    // reading: the body said "The open orders did not load" while the header
    // above it still read "Dallas · 0 open orders". The count is the same claim
    // the empty state makes, one line up.
    if (loadFailed) return branchName;
    if (isReceive) return `${branchName} · ${remainingCount} open order${remainingCount !== 1 ? 's' : ''}`;
    // #705, 79.22b.4.6.1c -- "1 item need attention": the verb follows the number too.
    if (isUse) return `${branchName} · ${remainingCount} item${remainingCount !== 1 ? 's' : ''} ${remainingCount === 1 ? 'needs' : 'need'} attention`;
    if (isRequest) return `${branchName} · ${remainingCount} item${remainingCount !== 1 ? 's' : ''} ${remainingCount === 1 ? 'needs' : 'need'} attention`;
    return `${branchName} · ${remainingCount} item${remainingCount !== 1 ? 's' : ''} ${remainingCount === 1 ? 'needs' : 'need'} audit`;
  }

  const badge = getHeaderBadge();
  const empty = getEmptyState();

  // #684 -- a list row's button for a made item, in Stock Home's words.
  function rowRequestLabel(item) {
    const r = requestRoute({ categories, categoryId: item.categoryId, viewerBranchId: userBranchId });
    if (r.route === 'ask_maker') return `Ask ${branchNameOf(r.makerBranchId)}`;
    if (r.route === 'make_own') return 'Make these';
    return null;
  }

  // #684 D-F -- where each line of the request is going, and what Send does.
  const lineRoute = l => requestRoute({ categories, categoryId: l.categoryId, viewerBranchId: userBranchId });
  const requestSplit = isRequest ? splitRequestLines(requestCart, { categories, viewerBranchId: userBranchId }) : null;
  const requestSummary = requestSplit ? requestSendSummary(requestSplit, branchNameOf) : null;
  // Tags appear only when something on the list is made: an all-purchase
  // request looks exactly as it always has.
  const cartHasMade = isRequest && requestCart.some(l => lineRoute(l).route !== 'buy');
  const woForAsks = !!(requestSplit && requestSplit.asks.length) && !!requestWorkOrderRef.trim() && workOrderRefError(requestWorkOrderRef.trim());
  const sentAsks = requestSent.filter(s => s.kind === 'ask');
  // The one-line view's quantity hints say WHICH number it was filled with.
  const singleToMaker = requestCart.length === 1
    && lineRoute(requestCart[0]).route !== 'buy' && !requestCart[0].buyAnyway;

  // #684 D and E -- where a line is going, and the switch between its maker and
  // buying. NOT A COMPONENT: locked rule 2 bans defining one inside a parent's
  // body, because it would remount every render; this returns elements from a
  // plain call in the same pass, as Stock Home's rowControl() does.
  function lineTagBlock(l, i, { single = false } = {}) {
    if (!cartHasMade || !l) return null;
    const r = lineRoute(l);
    const maker = branchNameOf(r.makerBranchId);
    const made = r.route !== 'buy';
    const toMaker = made && !l.buyAnyway;
    const minH = controlHeight(coarse, 'min');
    const link = {
      background: 'none', border: 'none', padding: '0 2px', minHeight: minH,
      font: 'inherit', fontSize: 12, color: '#3A3A3A', textDecoration: 'underline',
      textUnderlineOffset: 3, cursor: 'pointer', textAlign: 'left',
    };
    return (
      <div style={{ marginTop: single ? -10 : 4, marginBottom: single ? 12 : 0 }}>
        <span style={{
          display: 'inline-block', fontSize: 11, lineHeight: 1.35, borderRadius: 6, padding: '2px 6px',
          ...(toMaker ? LINE_TAG.make : LINE_TAG.buy),
        }}>
          {requestLineTag({ route: r.route, makerName: maker, buyAnyway: l.buyAnyway })}
        </span>
        {made && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 2 }}>
            {/* Our own shop's ask is its own sheet and its own write, so the
                line carries the way to it rather than riding Send. */}
            {r.route === 'make_own' && !l.buyAnyway && (
              <button type="button"
                onClick={() => openRouted({
                  itemId: l.itemId, name: l.itemName, uom: l.itemUom, sku: l.sku, categoryId: l.categoryId,
                  qtyOnHand: l.qtyOnHand, reorderPoint: l.reorderPoint, reorderQty: l.reorderQty,
                  ...r, fromCart: true,
                })}
                style={{
                  background: '#0F6E56', color: '#FFFFFF', border: 'none', borderRadius: 6,
                  padding: '0 10px', minHeight: minH, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                Make these ourselves
              </button>
            )}
            <button type="button"
              onClick={() => (single && l.buyAnyway ? askInstead() : setLineBuyAnyway(i, !l.buyAnyway))}
              style={link}>
              {l.buyAnyway
                ? (r.route === 'make_own' ? 'Make these ourselves instead' : `Ask ${maker} instead`)
                : 'Buy from a vendor instead'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // #684 -- a routed item has no activeItem by design; its own sheet is showing.
  // #374 -- and a refused adjustment arriving by scan skips the spinner: the
  // refusal below is the answer, so there is nothing to wait for.
  if (preloadItemId && !activeItem && !routedItem && !routedDone && !error && !adjustRefused) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: 13, color: '#9A9A9A' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>

      {/* #282, Session 78d. On a maximized desktop window this screen was a phone
          layout with nothing constraining it — roughly 1800px separated an item's
          name from the button that receives it, so confirming you are about to
          receive the right line meant crossing the screen and back. Picking the
          wrong line is the error class #279 exists to prevent, so the layout was
          working against that fix.

          Mobile is deliberately untouched — full-bleed is correct there and it is
          the priority surface. Every wrapper below is a no-op under 768px. */}
      <div style={{ background: '#0F0F0F', position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto' }}>
        <button onClick={() => setActive('inv_home')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: 0, display: 'flex', alignItems: 'center' }}>
          <IconArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
            {isReceive ? 'Receive stock' : isUse ? 'Use stock' : isRequest ? (
              // #684 -- the bar names the sheet it sits behind.
              routedItem?.route === 'ask_maker' ? `Ask ${branchNameOf(routedItem.makerBranchId)}`
                : routedItem?.route === 'make_own' ? 'Make these ourselves'
                  : 'Request restock'
            ) : 'Adjust count'}
          </div>
          <div style={{ fontSize: 11, color: '#9A9A9A' }}>{getHeaderSubtitle()}</div>
        </div>
        {/* #280, Session 78d. This read `preloadItemId` alone and therefore said
            "Scanned" for a tapped low-stock card, a searched catalog hit and the
            item detail page's own New request button — three of the four routes
            that preselect an item. It is the only thing on this header claiming
            HOW the item got here, and on the receive path a receiver could
            reasonably read it as "this was confirmed by scanning the label",
            which is exactly the assurance it does not provide.

            Nothing is shown for the other routes rather than inventing a second
            badge: the subtitle beside it already names the item, so a non-scan
            preselection needs no label at all. */}
        {preloadItemId && preloadSource === 'scan' && (
          <span style={{ background: '#1a1a1a', border: '0.5px solid #333', color: '#39D353', fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10 }}>
            Scanned
          </span>
        )}
        {!preloadItemId && !viewAll && badge && (
          <span style={{ background: badge.bg, color: badge.color, fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10 }}>
            {badge.label}
          </span>
        )}
        {(!preloadItemId || requestAddingItem) && viewAll && (
          <button onClick={() => {
            if (isRequest && requestAddingItem) { setRequestAddingItem(false); }
            else { setViewAll(false); setSearch(''); }
          }}
            style={{ background: 'none', border: '0.5px solid #444', borderRadius: 8, color: '#9A9A9A', fontSize: 10, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {isRequest && requestAddingItem ? 'Back to request' : getHeaderBackLabel()}
          </button>
        )}
      </div>
      </div>

      {/* #374 -- WHEN THE RULE REFUSES, THE SCREEN SAYS SO AND OFFERS NOTHING TO
          PRESS THAT WOULD FAIL. The header above stays, so the person still knows
          where they are; the search, the list and every sheet below are replaced
          by one sentence and a way back. Built from this screen's own parts: the
          panel is the load-failed panel's shape and colours, the button its
          Try again button. Nobody sees it today -- every role may adjust (#369). */}
      {adjustRefused ? (
        <div style={{ padding: '10px 12px', maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          <div role="alert" style={{ background: '#FFF6F5', borderRadius: 10, padding: '24px 16px', border: '1px solid #F3D3CE', textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 4 }}>
              You can't adjust counts
            </div>
            <div style={{ fontSize: 12, color: '#5C5C5C', marginBottom: 12 }}>
              {ADJUST_REFUSED}
            </div>
            <button
              onClick={() => setActive('inv_home')}
              style={{
                background: '#fff', border: '1px solid #C9C9C9', borderRadius: 8,
                padding: '0 18px', minHeight: controlHeight(coarse, 'min'),
                fontSize: 13, fontWeight: 600, color: '#0F0F0F', cursor: 'pointer',
              }}
            >
              Back to stock
            </button>
          </div>
        </div>
      ) : (<>

      {/* #282. Search bar: white band spans the viewport, its field tracks the column.

          #604 D1 and D4, Session 79.21c. The condition was `viewAll || isReceive`,
          so Receive stock had a search box on its default view all along and Use
          stock, Request restock and Adjust count had NONE -- the only route to an
          item that was not low was a button below every low row. Measured on a
          phone at 375x812 with 26 low items: the page is 2,134px and that button
          begins at 1,930px, so 2.4 screens of scrolling to reach the control that
          lets you search.

          Now unconditional on these four screens, and STICKY (D4): a search field
          that scrolls away has solved the first screen and nothing after it.
          Material Design's word for the standard is "persistent", and SAP Fiori's
          list report puts the basic search first in the header ahead of every
          other filter control.

          #540 found one CSS line that had disabled position:sticky for every
          in-page header in the app. That is fixed, and this is the first new
          sticky header written since, so it is verified on the live screen rather
          than assumed. */}
      {(!preloadItemId || requestAddingItem) && (
        <div style={{ position: 'sticky', top: 0, zIndex: 5, padding: '10px 12px', background: 'white', borderBottom: '1px solid #E8E8E8', display: 'flex', gap: 8, maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {/* #604 callout D -- 36px was below #417 D2's 40px floor with a finger.
              The input itself is left unstyled so it inherits index.css's 16px
              floor for every text-entry field (locked rule 12, iOS auto-zoom). */}
          {/* #604, corrected on Colby's eye at 79.21c: the Browse all items link
              ran PAST the right edge of the white band once somebody started
              typing. Classic flexbox: a flex item defaults to min-width:auto, so
              this field refused to shrink below the input's own intrinsic width
              and pushed the link out of the container. minWidth 0 on BOTH the
              field and the input is what lets it shrink. */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, background: '#F5F5F3', borderRadius: 8, padding: '0 10px', height: 44 }}>
            <IconSearch size={14} color="#9A9A9A" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={isReceive ? 'Search item or any order number...' : 'Search any item...'}
              style={{ flex: 1, minWidth: 0, width: '100%', border: 'none', background: 'none', color: '#0F0F0F', outline: 'none' }} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: 0, display: 'flex' }}><IconX size={13} /></button>}
          </div>
          {/* #604 D5 -- Browse all items moves up here from below the list, and
              is renamed: after D2, typing already shows all items, so "Show all
              items" named a mode that has stopped being a separate state.
              Browsing and searching are different intents and D2 only covers
              one -- somebody who does not know the item's name still needs a way
              in. A link rather than a button because it changes what you are
              looking at rather than committing anything. */}
          {!viewAll && (
            <button
              onClick={() => { setSearch(''); setViewAll(true); }}
              style={{ flexShrink: 0, background: 'none', border: 'none', color: '#1F6FBF', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', padding: '0 4px', minHeight: 44 }}
            >
              Browse all items
            </button>
          )}
        </div>
      )}
      {/* #604 callout A -- the sentence naming WHAT THE LIST IS lives in the
          header subtitle, which already renders above this band and already
          carried the low-item count. getHeaderSubtitle() gained the search
          scope rather than a second line being added here: one sentence, one
          place, and two copies of it would drift. */}

      {error && <div style={{ margin: isMobile ? '10px 12px' : '10px auto', maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>{error}</div>}
      {isRequest && categoriesFailed && (!preloadItemId || requestAddingItem) && (
        <div role="status" style={{ margin: isMobile ? '10px 12px 0' : '10px auto 0', maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, background: '#FFF8EC', border: '1px solid #F3D9A8', color: '#8A5A08', borderRadius: 8, padding: '8px 10px', fontSize: 13, lineHeight: 1.4 }}>
          {MAKE_CHECK_FAILED}
        </div>
      )}

      {(!preloadItemId || requestAddingItem) && (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {/* #684 -- in request mode the list waits for the category tree too,
              so a made row never flashes "+ Request" before it says "Ask Houston". */}
          {(loading || (isRequest && !categoriesReady)) ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : loadFailed ? (
            // #595 -- BEFORE every empty state and before the search branch:
            // when the load failed we know nothing about this branch, so every
            // sentence below would be a claim we cannot make.
            <div style={{ background: '#FFF6F5', borderRadius: 10, padding: '24px 16px', border: '1px solid #F3D3CE', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 4 }}>
                {isReceive ? 'The open orders did not load'
                  : isAdjust ? 'The audit list did not load'
                  : 'The item list did not load'}
              </div>
              <div style={{ fontSize: 12, color: '#5C5C5C', marginBottom: 12 }}>
                Check your connection. Nothing has been changed.
              </div>
              <button
                onClick={loadDefaultItems}
                style={{
                  background: '#fff', border: '1px solid #C9C9C9', borderRadius: 8,
                  padding: '0 18px', minHeight: controlHeight(coarse, 'min'),
                  fontSize: 13, fontWeight: 600, color: '#0F0F0F', cursor: 'pointer',
                }}
              >
                Try again
              </button>
            </div>
          ) : displayItems.length === 0 && search.trim() ? (
            // #312. Must come BEFORE the empty-state branch below. A search that
            // matches nothing is not the same as having no open orders, and
            // showing "No open orders" to someone who mistyped a PO would be a
            // false statement about the state of the branch.
            <div style={{ padding: 24, textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>No items match your search.</div>
          ) : displayItems.length === 0 && !showingAll ? (
            <div style={{ background: 'white', borderRadius: 10, padding: '24px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#39D353', marginBottom: 4 }}>{empty.title}</div>
              <div style={{ fontSize: 12, color: '#9A9A9A' }}>{empty.sub}</div>
            </div>
          ) : displayItems.length === 0 && showingAll ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>No items match your search.</div>
          ) : (
            displayItems.map(item => (
              <ItemRow
                key={`${keyOf(item)}-${item.requestNumber || ''}`}
                item={item}
                isCompleted={!!completedIds[keyOf(item)]}
                doneInfo={completedIds[keyOf(item)]}
                isReceive={isReceive}
                isAdjust={isAdjust}
                // #604 -- what a row looks like follows WHAT IS SHOWN, not
                // whether the browse button was pressed. A well-stocked item
                // found by typing must not be painted as a low-stock row.
                viewAll={showingAll}
                isRequest={isRequest}
                isRequestAdd={isRequest && requestAddingItem}
                requestLabel={isRequest && !requestAddingItem ? rowRequestLabel(item) : null}
                onOpen={isRequest && requestAddingItem ? addItemToCart : openItem}
              />
            ))
          )}

          {/* #604 D5 -- this used to be the ONLY way to reach an item that was
              not low, and it sat under every low row. It moves up beside the
              search field; what is left here is nothing. */}
        </div>
      )}

      {showScanner && (
        <QRScanOverlay onScan={handleCartScan} onClose={() => setShowScanner(false)} />
      )}

      {/* #684 -- the purchase sheet stays up while a split send is taking lines
          off the list and through to its done screen, and steps aside while a
          made item's own sheet or done card is showing. */}
      {(activeItem || (isRequest && (requestCart.length > 0 || requestSaving || requestDone)))
        && !requestAddingItem && !(isRequest && (routedItem || routedDone)) && (
        <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={560} onClose={closeSheet}>
          {/* #164, worked around rather than fixed. Overlay's sheet variant already
              owns the scroll (maxHeight 90vh + overflowY auto); its modal variant sets
              overflow:hidden with no maxHeight, so a tall form would clip with no way
              to reach the submit button. The child owns the scroll on desktop only -
              applying it on both would nest two scrollers on mobile. The real fix is
              inside Overlay.js and belongs to #164 on Sep 8-19. */}
          <div style={{
            padding: isMobile ? '20px 20px 40px' : '24px 24px 28px',
            ...(isMobile ? {} : { maxHeight: '85vh', overflowY: 'auto' }),
          }}>
            {/* The grab handle reads as "drag me down to dismiss" and is meaningless
                on a centred desktop modal, which dismisses on backdrop click or Escape. */}
            {isMobile && (
              <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '0 auto 16px' }} />
            )}

            {isReceive && (
              <ReceiveSheetContent
                itemName={activeItem?.name || ''}
                itemId={activeItem?.itemId ?? null}
                itemUom={activeItem?.uom || null}
                branchName={branchName}
                matchedRequest={receiveMatchedRequest}
                canOverride={canOverrideJobRef}
                printDescription={
                  // #509 -- REUSING the existing "printing unavailable" state
                  // rather than inventing a second one. For an off-catalog line
                  // printing genuinely IS unavailable: submitReceiveDelivery
                  // skips label printing when a line has no catalog item, and
                  // materialReceive.test.js has asserted that since #333. The
                  // toggle previously sat ON promising a label that could never
                  // be produced -- a promise the system cannot keep.
                  activeItem?.isOffCatalog
                    ? 'Off-catalog items carry no catalog record to put on a label. Add it to the catalog first if you need one.'
                    : receivePrintDescription
                }
                printAvailable={!!receivePrintOption && !activeItem?.isOffCatalog}
                isOffCatalog={!!activeItem?.isOffCatalog}
                packQty={receivePack.packQty}
                packUnit={receivePack.packUnit}
                packVendorName={receivePack.vendorName}
                onSubmit={submitReceive}
                onCancel={closeSheet}
                saving={receiveSaving}
                done={receiveDone}
                orderClosed={receiveOrderClosed}
                photoFailed={receivePhotoFailed}
                serverError={receiveError}
              />
            )}
                 

            {isUse && (
              useDone ? (
                <>
                  <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Stock updated</div>
                    <div style={{ fontSize: 12, color: '#5C5C5C' }}>{useWillGoNegative ? 'Stock went negative. Your manager has been notified.' : 'Inventory count has been updated.'}</div>
                  </div>
                  <button onClick={closeSheet} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Use stock</div>
                  <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>
                    {activeItem.name} · {branchName} · <span style={{ fontWeight: 700, color: useWillGoNegative ? '#A32D2D' : '#0F0F0F' }}>{activeItem.qtyOnHand} on hand</span>
                  </div>
                  {useError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{useError}</div>}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Qty used</div>
                  {/* #760 - the shared stepper (approved drawing, section 3). */}
                  <QtyStepper value={String(useQty ?? '')} onChange={setUseQty} min={1} label="Qty used"
                    boxStyle={fieldBox(80, 44, 22, useWillGoNegative ? NEGATIVE_BOX : null)} rowStyle={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: useWillGoNegative ? '#A32D2D' : '#1D9E75', fontWeight: 700, flexShrink: 0 }}>→ {useNewQty} remaining</div>
                  </QtyStepper>
                  {useWillGoNegative && (
                    <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <IconAlertTriangle size={15} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D' }}>Stock will go negative</div>
                        <div style={{ fontSize: 11, color: '#791F1F', marginTop: 1 }}>Your manager will be notified to audit this item. You can still submit.</div>
                      </div>
                    </div>
                  )}
                  {/* #349 and #199 part 1 (approved drawing D, D2-D3): the rule is
                      stated once above the pair, in the words the save refuses with.
                      It was "(optional)" on both here while the item page asked for one. */}
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 10 }}>
                    {requireJobRefOnUse
                      ? <>A <strong style={{ color: '#5C5C5C' }}>WO#</strong> is required.</>
                      : <>Enter a <strong style={{ color: '#5C5C5C' }}>WO#</strong> or a <strong style={{ color: '#5C5C5C' }}>note</strong>, at least one is required.</>}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', marginBottom: 6 }}>WO#{requireJobRefOnUse ? ' *' : ''}</div>
                  <input type="text" value={useJobRef} onChange={e => { setUseJobRef(e.target.value); fetchJobHistory(e.target.value); }} placeholder="e.g. 123456 or 123456-7"
                    style={{ width: '100%', height: 40, border: (workOrderRefError(useJobRef) || useError === USE_NEEDS_WO) ? '1.5px solid #A32D2D' : '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', marginBottom: workOrderRefError(useJobRef) ? 4 : (useJobHistory ? 8 : 16), boxSizing: 'border-box' }} />
                  {/* #499 - beside the field, as they type. */}
                  {workOrderRefError(useJobRef) && (
                    <div style={{ fontSize: 11, color: '#A32D2D', marginBottom: 12 }}>{WORK_ORDER_REF_HINT}</div>
                  )}
                  {useJobHistoryLoading && <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 12 }}>Looking up job history...</div>}
                  {useJobHistory && !useJobHistoryLoading && (
                    <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 8, padding: '10px 12px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <IconHistory size={15} color="#3C3489" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#3C3489' }}>{useJobHistory.totalUsed} {activeItem.uom} already used on {useJobRef.trim()}</div>
                        <div style={{ fontSize: 11, color: '#534AB7', marginTop: 1 }}>{useJobHistory.txCount} transaction{useJobHistory.txCount !== 1 ? 's' : ''} · last pull {fmtDate(useJobHistory.lastDate)}</div>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', marginBottom: 6 }}>Notes{requireJobRefOnUse && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--ink-3)' }}> (optional)</span>}</div>
                  <input type="text" value={useNotes} onChange={e => setUseNotes(e.target.value)} placeholder="e.g. cut for face panel"
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }} />
                  <button onClick={submitUse} disabled={useSaving}
                    style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: useSaving ? '#E0E0E0' : useWillGoNegative ? '#A32D2D' : '#0F0F0F', color: useSaving ? '#9A9A9A' : 'white', cursor: useSaving ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
                    {useSaving ? 'Saving...' : useWillGoNegative ? 'Submit anyway' : 'Confirm use'}
                  </button>
                  <button onClick={closeSheet} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
                  <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>Updates stock count immediately</div>
                </>
              )
            )}

            {isAdjust && (
              adjustDone ? (
                <>
                  <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Count adjusted</div>
                    {/* #369 — this said "your manager has been notified" unconditionally.
                        Opening count no longer notifies, so on the one path Dallas walks
                        on Sep 1 that sentence was about to become false on every save. */}
                    {/* #363 D17 -- from what the save did: "notified" only when an
                        alert was delivered. */}
                    <div style={{ fontSize: 12, color: '#5C5C5C' }}>
                      {adjustDoneText || (adjustReason === REASON_OPENING_COUNT
                        ? 'Inventory updated and recorded in the ledger.'
                        : 'Inventory updated and your manager has been notified.')}
                    </div>
                  </div>
                  <button onClick={closeSheet} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Adjust count</div>
                  <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 12 }}>{activeItem.name} · {branchName}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 6, padding: '3px 10px', fontSize: 12, color: '#A32D2D', fontWeight: 700 }}>
                      System: {activeItem.qtyOnHand}
                    </div>
                    <div style={{ fontSize: 13, color: '#7A7A7A' }}>→ enter actual count below</div>
                  </div>
                  {adjustError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{adjustError}</div>}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Actual qty on hand</div>
                  {/* #760 - the shared stepper (approved drawing, section 3). */}
                  <QtyStepper value={String(adjustQty ?? '')} onChange={setAdjustQty} min={0} label="Actual qty on hand"
                    boxStyle={fieldBox(90, 52, 26)} rowStyle={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: '#7A7A7A', flex: 1, lineHeight: 1.4 }}>Enter the number you physically counted</div>
                  </QtyStepper>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Reason *</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                    {ADJUST_REASONS.map(reason => (
                      <button key={reason} onClick={() => setAdjustReason(reason)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: `1px solid ${adjustReason === reason ? '#1D9E75' : '#E0E0E0'}`, background: adjustReason === reason ? '#E1F5EE' : '#F8F8F8', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${adjustReason === reason ? '#1D9E75' : '#C0C0C0'}`, background: adjustReason === reason ? '#1D9E75' : 'white', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {adjustReason === reason && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
                        </div>
                        <span style={{ fontSize: 13, color: adjustReason === reason ? '#085041' : '#0F0F0F', fontWeight: adjustReason === reason ? 700 : 400 }}>{reason}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span></div>
                  <input type="text" value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)} placeholder="e.g. counted during morning walkthrough"
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }} />
                  {/* #363 D3 -- whether the managers will be notified, said before Confirm. */}
                  <AdjustAlertNotice itemId={activeItem.itemId} branchId={userBranchId} branchName={branchName}
                    onHand={activeItem.qtyOnHand} counted={adjustQty} reason={adjustReason} canSeeCost={canSeeCost} />
                  <button onClick={submitAdjust} disabled={adjustSaving}
                    style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: adjustSaving ? '#E0E0E0' : '#0F0F0F', color: adjustSaving ? '#9A9A9A' : 'white', cursor: adjustSaving ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
                    {adjustSaving ? 'Saving...' : 'Confirm adjustment'}
                  </button>
                  <button onClick={closeSheet} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
                  <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>
                    Logged as {profile?.full_name} · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </>
              )
            )}

            {isRequest && (
              requestDone ? (
                <>
                  <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                    {/* #684 F -- the done screen names every request the send made
                        and who approves each, in the order they went. A send to
                        Purchasing alone reads exactly as it always has. */}
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>
                      {sentAsks.length === 0 ? 'Request submitted'
                        : requestSent.length > 1 ? `${requestSent.length} requests sent`
                          : `${branchNameOf(sentAsks[0].makerBranchId)} has been asked`}
                    </div>
                    {sentAsks.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#5C5C5C' }}>Your manager has been notified and will place the order.</div>
                    ) : requestSent.map((s, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#3A3A3A', marginTop: 4, lineHeight: 1.45 }}>
                        {s.kind === 'ask'
                          ? `${branchNameOf(s.makerBranchId)}: ${s.lines.length} item${s.lines.length === 1 ? '' : 's'} to make. Your manager approves it first, then ${branchNameOf(s.makerBranchId)} confirms.`
                          : `Purchasing: ${s.lines.length} item${s.lines.length === 1 ? '' : 's'} to buy${s.requestNumber ? ` on ${s.requestNumber}` : ''}. Your manager approves it, then Purchasing orders it.`}
                      </div>
                    ))}
                  </div>
                  <button onClick={closeSheet} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>
                    {requestCart.length > 1 ? 'Request materials' : 'Request restock'}
                  </div>
                  {/* #687 -- the on-hand count comes from the LINE, which carries
                      its own. This read activeItem.qtyOnHand, and a one-item
                      Request several has no active item: the whole app went
                      blank. A line with no count leaves the figure out rather
                      than printing "undefined on hand". */}
                  <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>
                    {requestCart.length > 1
                      ? `${requestCart.length} items · ${branchName}`
                      : [requestCart[0]?.itemName, branchName, requestCart[0]?.qtyOnHand != null ? `${requestCart[0].qtyOnHand} on hand` : null].filter(Boolean).join(' · ')}
                  </div>
                  {requestCart.length === 1 && lineTagBlock(requestCart[0], 0, { single: true })}
                  {categoriesFailed && (
                    <div role="status" style={{ background: '#FFF8EC', border: '1px solid #F3D9A8', color: '#8A5A08', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.4, marginBottom: 12 }}>
                      {MAKE_CHECK_FAILED}
                    </div>
                  )}
                  {requestNotice && (
                    <div role="status" style={{ background: '#EAF5F0', border: '1px solid #CFE6DA', color: '#14503B', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.4, marginBottom: 12 }}>
                      {requestNotice}
                    </div>
                  )}
                  {requestError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{requestError}</div>}

                  {requestCart.length === 1 ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Qty to request</div>
                      {/* #760 - the shared stepper (approved drawing, section 4). A
                          request line always holds a number: an emptied box goes
                          back to 1, as it always has. */}
                      <QtyStepper value={String(requestCart[0].qty)} min={1} label="Qty to request"
                        onChange={v => updateCartQty(0, v === '' ? 1 : parseInt(v, 10))}
                        boxStyle={fieldBox(80, 44, 22, REQUEST_BOX)} rowStyle={{ marginBottom: 4 }}>
                        {/* #684 C -- a line going to its maker was filled with
                            what the branch is SHORT by, not the buy size, so the
                            hint says which number it is (#615). */}
                        <div style={{ fontSize: 11, color: '#7A7A7A', flex: 1, lineHeight: 1.4 }}>
                          {singleToMaker
                            ? (requestCart[0].shortBy > 0 ? 'Pre-filled with what you are short' : 'No reorder point set')
                            : (requestCart[0].reorderQty ? 'Pre-filled from reorder qty' : 'No reorder qty set')}
                        </div>
                      </QtyStepper>
                      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 16, paddingLeft: 2 }}>
                        {singleToMaker
                          ? (requestCart[0].shortBy > 0 ? `You are short ${requestCart[0].shortBy}` : 'Enter amount needed')
                          : (requestCart[0].reorderQty ? `Reorder qty: ${requestCart[0].reorderQty}` : 'Enter amount needed')}
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
                      {requestCart.map((item, i) => (
                        <div key={item.itemId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8F8F8', borderRadius: 10, padding: '8px 6px 8px 12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.itemName}</div>
                            <div style={{ fontSize: 11, color: '#9A9A9A' }}>{item.itemUom}</div>
                            {lineTagBlock(item, i)}
                          </div>
                          {/* #760, #793 - the shared stepper (approved drawing,
                              section 4): 30 to 40 with a finger, and the box's type
                              16px so an iPhone does not zoom. Wrapped so "Whole
                              numbers only." lands under the buttons, not beside them. */}
                          <div style={{ flexShrink: 0 }}>
                            <QtyStepper value={String(item.qty)} min={1} label={`Qty to request, ${item.itemName}`}
                              onChange={v => updateCartQty(i, v === '' ? 1 : parseInt(v, 10))}
                              boxStyle={CART_BOX} rowStyle={{ gap: 6 }} />
                          </div>
                          {/* #513, amended at 79.22b.6.3.2 - a 16px icon beside a 40px +
                              is a line deleted by a finger reaching for +. The icon
                              stays 16; the place to press is the control's size. */}
                          <button onClick={() => removeCartItem(i)} aria-label="Remove item"
                            style={{ width: stepH, height: stepH, padding: 0, background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconTrash size={16} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: requestCart.length === 1 ? 0 : 8, marginBottom: 16 }}>
                    <button onClick={startAddItemViaSearch} style={{ flex: 1, padding: '10px 0', background: 'none', border: '1px dashed #C0C0C0', borderRadius: 10, fontSize: 12, color: '#5C5C5C', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <IconSearch size={14} /> Search catalog
                    </button>
                    <button onClick={startAddItemViaScan} style={{ flex: 1, padding: '10px 0', background: 'none', border: '1px dashed #C0C0C0', borderRadius: 10, fontSize: 12, color: '#5C5C5C', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <IconCamera size={14} /> Scan item
                    </button>
                  </div>

                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
                    WO# <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
                  </div>
                  <div style={{ position: 'relative', marginBottom: woForAsks ? 4 : 16 }}>
                    <IconBriefcase size={14} color="#9A9A9A" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                    <input type="text" value={requestWorkOrderRef} onChange={e => setRequestWorkOrderRef(e.target.value)} placeholder="e.g. 123456 or 123456-1"
                      style={{ width: '100%', height: 40, border: woForAsks ? '1.5px solid #A32D2D' : '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px 0 32px', color: '#0F0F0F', boxSizing: 'border-box' }} />
                  </div>
                  {/* #684 -- an ask carries a work order the database checks, so
                      the rule is said beside the field while an ask is on the list. */}
                  {woForAsks && (
                    <div style={{ fontSize: 11, color: '#A32D2D', marginBottom: 12 }}>{WORK_ORDER_REF_HINT}</div>
                  )}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span></div>
                  <textarea value={requestNotes} onChange={e => setRequestNotes(e.target.value)} placeholder="Any additional context..." rows={3}
                    style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 10, padding: '10px 12px', color: '#0F0F0F', marginBottom: requestSummary ? 12 : 20, boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 16, resize: 'vertical' }} />
                  {/* #684 F -- SAID BEFORE ANYTHING IS SENT, in the words and the
                      shape RequestTransferSheet's D69 notice already uses: how many
                      requests Send makes, and to whom. */}
                  {requestSummary && (
                    <div style={{
                      marginBottom: 12, background: '#FDF3E3', borderLeft: '3px solid #EF9F27',
                      borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
                    }}>
                      {requestSummary.sentence}
                    </div>
                  )}
                  <button onClick={submitRequest} disabled={requestSaving}
                    style={{ width: '100%', padding: 14, minHeight: controlHeight(coarse, 'commit'), borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: requestSaving ? '#E0E0E0' : '#1D9E75', color: requestSaving ? '#9A9A9A' : 'white', cursor: requestSaving ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
                    {requestSaving ? 'Submitting...'
                      : !requestSummary ? 'Submit request'
                        : requestSummary.count > 1 ? `Send ${requestSummary.count} requests`
                          : `Ask ${branchNameOf(requestSplit.asks[0].makerBranchId)}`}
                  </button>
                  <button onClick={closeSheet} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
                  {!requestSummary && (
                    <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>Goes to your branch manager for approval</div>
                  )}
                </>
              )
            )}
          </div>
        </Overlay>
      )}

      {/* #684 A and B -- A MADE ITEM'S OWN SHEET, from whichever door opened it:
          the Ask sheet Stock Home's chooser leads to, carrying "Buy from a
          vendor instead?" at its foot because there is no chooser behind it
          here. Keyed on the item so every opening value is that item's (#516). */}
      {isRequest && routedItem?.route === 'ask_maker' && (
        <RequestTransferSheet
          key={`ask:${routedItem.itemId}`}
          visible
          isMobile={isMobile}
          item={{
            id: routedItem.itemId, name: routedItem.name, sku: routedItem.sku ?? null,
            uom: routedItem.uom ?? null, category_id: routedItem.categoryId ?? null,
          }}
          categories={categories}
          branches={BRANCHES}
          elsewhere={[]}
          myBranchId={userBranchId}
          myUserId={profile?.id}
          myDepartmentId={profile?.department_id ?? null}
          myUserName={profile?.full_name}
          // C -- the shortfall, as the Ask sheet proposes it from Stock Home.
          shortBy={rowShortBy({ qty_on_hand: routedItem.qtyOnHand, reorder_point: routedItem.reorderPoint ?? null })}
          onDone={onAskSent}
          onClose={closeRouted}
          onBuyInstead={buyInstead}
        />
      )}
      {isRequest && routedItem?.route === 'make_own' && (
        <MakeForShelfSheet
          key={`make:${routedItem.itemId}`}
          visible
          phoneLayout={phoneLayout}
          coarse={coarse}
          row={{
            id: `req:${routedItem.itemId}`, item_id: routedItem.itemId, branch_id: userBranchId,
            qty_on_hand: routedItem.qtyOnHand ?? 0, reorder_point: routedItem.reorderPoint ?? null,
            reorder_qty: routedItem.reorderQty ?? null,
            inventory_items: { id: routedItem.itemId, name: routedItem.name, uom: routedItem.uom, sku: routedItem.sku ?? null },
          }}
          branchName={branchName}
          branchNames={BRANCH_NAMES}
          makerDepartmentId={routedItem.makerDepartmentId ?? null}
          openAsks={routedAsks}
          canConfirm={makeOwnCanConfirm(routedItem)}
          requesterName={profile?.full_name || null}
          onClose={closeRouted}
          onBuyInstead={buyInstead}
          onDone={onMakeOwnSent}
        />
      )}
      {isRequest && routedDone && (
        <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={480} onClose={finishRouted}>
          <div style={{ padding: isMobile ? '20px 20px 40px' : '24px 24px 28px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>{routedDone.title}</div>
              <div style={{ fontSize: 12, color: '#3A3A3A', lineHeight: 1.45 }}>{routedDone.sub}</div>
            </div>
            <button onClick={finishRouted}
              style={{ width: '100%', padding: 14, minHeight: controlHeight(coarse, 'commit'), borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </Overlay>
      )}
      </>)}
    </div>
  );
}